// src/FormApi.ts
import { Store } from "@tanstack/store";
import { functionalUpdate, getBy, isNonEmptyArray, setBy } from "./utils.js";
function getDefaultFormState(defaultState) {
  return {
    values: defaultState.values ?? {},
    fieldMeta: defaultState.fieldMeta ?? {},
    canSubmit: defaultState.canSubmit ?? true,
    isFieldsValid: defaultState.isFieldsValid ?? false,
    isFieldsValidating: defaultState.isFieldsValidating ?? false,
    isFormValid: defaultState.isFormValid ?? false,
    isFormValidating: defaultState.isFormValidating ?? false,
    isSubmitted: defaultState.isSubmitted ?? false,
    isSubmitting: defaultState.isSubmitting ?? false,
    isTouched: defaultState.isTouched ?? false,
    isValid: defaultState.isValid ?? false,
    isValidating: defaultState.isValidating ?? false,
    submissionAttempts: defaultState.submissionAttempts ?? 0,
    formValidationCount: defaultState.formValidationCount ?? 0
  };
}
var FormApi = class {
  constructor(opts) {
    // // This carries the context for nested fields
    this.options = {};
    this.fieldInfo = {};
    this.validationMeta = {};
    this.update = (options) => {
      if (!options)
        return;
      this.store.batch(() => {
        const shouldUpdateValues = options.defaultValues && options.defaultValues !== this.options.defaultValues && !this.state.isTouched;
        const shouldUpdateState = options.defaultState !== this.options.defaultState && !this.state.isTouched;
        this.store.setState(
          () => getDefaultFormState(
            Object.assign(
              {},
              this.state,
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              shouldUpdateState ? options.defaultState : {},
              // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
              shouldUpdateValues ? {
                values: options.defaultValues
              } : {}
            )
          )
        );
      });
      this.options = options;
    };
    this.reset = () => this.store.setState(
      () => getDefaultFormState({
        ...this.options.defaultState,
        values: this.options.defaultValues ?? this.options.defaultState?.values
      })
    );
    this.validateAllFields = async (cause) => {
      const fieldValidationPromises = [];
      this.store.batch(() => {
        void Object.values(this.fieldInfo).forEach((field) => {
          Object.values(field.instances).forEach((instance) => {
            if (!instance.state.meta.isTouched) {
              instance.setMeta((prev) => ({ ...prev, isTouched: true }));
              fieldValidationPromises.push(
                Promise.resolve().then(() => instance.validate(cause))
              );
            }
          });
        });
      });
      return Promise.all(fieldValidationPromises);
    };
    this.handleSubmit = async () => {
      this.store.setState((old) => ({
        ...old,
        // Submission attempts mark the form as not submitted
        isSubmitted: false,
        // Count submission attempts
        submissionAttempts: old.submissionAttempts + 1
      }));
      if (!this.state.canSubmit)
        return;
      this.store.setState((d) => ({ ...d, isSubmitting: true }));
      const done = () => {
        this.store.setState((prev) => ({ ...prev, isSubmitting: false }));
      };
      await this.validateAllFields("submit");
      if (!this.state.isFieldsValid) {
        done();
        this.options.onSubmitInvalid?.(this.state.values, this);
        return;
      }
      if (!this.state.isValid) {
        done();
        this.options.onSubmitInvalid?.(this.state.values, this);
        return;
      }
      try {
        await this.options.onSubmit?.(this.state.values, this);
        this.store.batch(() => {
          this.store.setState((prev) => ({ ...prev, isSubmitted: true }));
          done();
        });
      } catch (err) {
        done();
        throw err;
      }
    };
    this.getFieldValue = (field) => getBy(this.state.values, field);
    this.getFieldMeta = (field) => {
      return this.state.fieldMeta[field];
    };
    this.getFieldInfo = (field) => {
      return this.fieldInfo[field] ||= {
        instances: {}
      };
    };
    this.setFieldMeta = (field, updater) => {
      this.store.setState((prev) => {
        return {
          ...prev,
          fieldMeta: {
            ...prev.fieldMeta,
            [field]: functionalUpdate(updater, prev.fieldMeta[field])
          }
        };
      });
    };
    this.setFieldValue = (field, updater, opts) => {
      const touch = opts?.touch;
      this.store.batch(() => {
        if (touch) {
          this.setFieldMeta(field, (prev) => ({
            ...prev,
            isTouched: true
          }));
        }
        this.store.setState((prev) => {
          return {
            ...prev,
            values: setBy(prev.values, field, updater)
          };
        });
      });
    };
    this.pushFieldValue = (field, value, opts) => {
      return this.setFieldValue(
        field,
        (prev) => [...Array.isArray(prev) ? prev : [], value],
        opts
      );
    };
    this.insertFieldValue = (field, index, value, opts) => {
      this.setFieldValue(
        field,
        (prev) => {
          return prev.map(
            (d, i) => i === index ? value : d
          );
        },
        opts
      );
    };
    this.removeFieldValue = (field, index, opts) => {
      this.setFieldValue(
        field,
        (prev) => {
          return prev.filter(
            (_d, i) => i !== index
          );
        },
        opts
      );
    };
    this.swapFieldValues = (field, index1, index2) => {
      this.setFieldValue(field, (prev) => {
        const prev1 = prev[index1];
        const prev2 = prev[index2];
        return setBy(setBy(prev, `${index1}`, prev2), `${index2}`, prev1);
      });
    };
    this.store = new Store(
      getDefaultFormState({
        ...opts?.defaultState,
        values: opts?.defaultValues ?? opts?.defaultState?.values,
        isFormValid: true
      }),
      {
        onUpdate: () => {
          let { state } = this.store;
          const fieldMetaValues = Object.values(state.fieldMeta);
          const isFieldsValidating = fieldMetaValues.some(
            (field) => field?.isValidating
          );
          const isFieldsValid = !fieldMetaValues.some(
            (field) => field?.errorMap && isNonEmptyArray(Object.values(field.errorMap).filter(Boolean))
          );
          const isTouched = fieldMetaValues.some((field) => field?.isTouched);
          const isValidating = isFieldsValidating || state.isFormValidating;
          const isFormValid = !state.formError;
          const isValid = isFieldsValid && isFormValid;
          const canSubmit = state.submissionAttempts === 0 && !isTouched || !isValidating && !state.isSubmitting && isValid;
          state = {
            ...state,
            isFieldsValidating,
            isFieldsValid,
            isFormValid,
            isValid,
            canSubmit,
            isTouched
          };
          this.store.state = state;
          this.state = state;
        }
      }
    );
    this.state = this.store.state;
    this.update(opts || {});
  }
};
export {
  FormApi
};
//# sourceMappingURL=FormApi.js.map